import { create } from "zustand";
import { axiosInstance } from "../lib/axios";
import toast from "react-hot-toast";

export interface Bid {
  _id: string;
  jobTitle?: string;
  jobLink?: string;
  platformAccount: string;
  connectsUsed?: number;
  totalConnects?: number;
  entryType?: "New Job" | "Invitation" | "Directly Awarded";
  submittedAt?: string;
  connectRate?: number;
  connectCost?: number;
  freelanceAccount?: any;
  bidAmount?: number;
  bidType?: "Fixed" | "Hourly";
  bidStatus?: string;
  wasNegotiated?: boolean;
  connectsRefunded?: boolean;
  createdBy?: { _id: string; fullName?: string; email?: string } | string;
  boostUsed?: boolean;
  boostConnects?: number;
  boostPosition?: number | null;
  notes?: string;
  screenshots?: string[];
  createdAt?: string;
  proposalLink?: string;
  projectRef?: any;
  contactRef?: any;
  submissionTimestamp?: string;
}

export interface JobCheckResult {
  status: "matched" | "not_found" | "invalid_url" | "completed" | "queued";
  jobId?: string;
  job?: any;
  ai?: any;
  hasAnalysis?: boolean;
  message?: string;
}

export interface UpworkAccount {
  _id: string;
  name: string;
  totalConnects: number;
  usedConnects: number;
  remainingConnects?: number;
  // Configuration-driven Freelance Account fields (platform derives from account)
  platform?: { _id: string; name: string; connectRate?: number } | null;
  assignedBd?: { _id: string; fullName?: string } | null;
  isActive?: boolean;
  connectRate?: number | null;
  effectiveConnectRate?: number;
  notes?: string;
}

interface BidState {
  bids: Bid[];
  accounts: UpworkAccount[];
  isFetching: boolean;
  isCreating: boolean;
  isUpdating: boolean;

  fetchMyBids: () => Promise<void>;
  fetchAccounts: () => Promise<void>;
  updateAccount: (id: string, totalConnects: number) => Promise<void>;
  createBid: (data: FormData | Partial<Bid>) => Promise<void>;
  updateBid: (id: string, data: Partial<Bid> | FormData) => Promise<void>;
  deleteBid: (id: string) => Promise<void>;
  setBids: (bids: Bid[]) => void;
  checkJobUrl: (url: string) => Promise<JobCheckResult>
  analyzeJob: (url: string) => Promise<JobCheckResult>;
}

export const useBidStore = create<BidState>((set, get) => ({
  bids: [],
  accounts: [],
  isFetching: false,
  isCreating: false,
  isUpdating: false,

  setBids: (bids) => set({ bids }),

  fetchMyBids: async () => {
    set({ isFetching: true });
    try {
      const res = await axiosInstance.get<Bid[]>("/bids/my");
      set({ bids: res.data });
    } catch (err: any) {
      console.error("fetchMyBids:", err);
      toast.error(err.response?.data?.message || "Failed to load bids");
    } finally {
      set({ isFetching: false });
    }
  },

  fetchAccounts: async () => {
    try {
      const res = await axiosInstance.get<UpworkAccount[]>("/bids/accounts");
      set({ accounts: res.data });
    } catch (err: any) {
      console.error("fetchAccounts:", err);
    }
  },

  updateAccount: async (id, totalConnects) => {
    try {
      const res = await axiosInstance.put(`/bids/accounts/${id}`, { totalConnects });
      set({
        accounts: get().accounts.map((a) => (a._id === id ? res.data : a)),
      });
      toast.success("Account updated");
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to update account");
    }
  },

  createBid: async (data) => {
    set({ isCreating: true });
    try {
      const headers =
        data instanceof FormData ? { "Content-Type": "multipart/form-data" } : {};
      const res = await axiosInstance.post("/bids", data, { headers });
      set({ bids: [res.data, ...get().bids] });
      toast.success("Bid saved");
    } catch (err: any) {
      console.error("createBid:", err);
      toast.error(err.response?.data?.message || "Failed to submit bid");
      throw err;
    } finally {
      set({ isCreating: false });
    }
  },

  updateBid: async (id, data) => {
    set({ isUpdating: true });
    try {
      const headers =
        data instanceof FormData ? { "Content-Type": "multipart/form-data" } : {};
      const res = await axiosInstance.put(`/bids/${id}`, data, { headers });
      set({ bids: get().bids.map((b) => (b._id === id ? res.data : b)) });
      toast.success("Bid updated");
    } catch (err: any) {
      console.error("updateBid:", err);
      toast.error(err.response?.data?.message || "Failed to update bid");
      throw err;
    } finally {
      set({ isUpdating: false });
    }
  },

  deleteBid: async (id) => {
    try {
      await axiosInstance.delete(`/bids/${id}`);
      set({ bids: get().bids.filter((b) => b._id !== id) });
      toast.success("Bid deleted");
    } catch (err: any) {
      console.error("deleteBid:", err);
      toast.error(err.response?.data?.message || "Failed to delete bid");
    }
  },

  checkJobUrl: async (url) => {
    const res = await axiosInstance.post<JobCheckResult>("/upwork-jobs/check", { url });
    return res.data;
  },

  analyzeJob: async (url) => {
    try {
      const res = await axiosInstance.post<JobCheckResult>("/upwork-jobs/analyze", { url });
      if (res.data.status === "completed") {
        toast.success("Analysis complete");
      } else {
        toast.success(res.data.message || "Analysis queued");
      }
      return res.data;
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to analyze job");
      throw err;
    }
  },

}));
